import React, { useState } from "react";
import { X, Crown, Shield, Calendar, AlertCircle, Check } from "lucide-react";
import { adminSubscriptionService, UpgradeUserPlanRequest, DowngradeUserPlanRequest } from "../services/adminSubscriptionService";
import { toast } from "react-hot-toast";

interface UserInterface {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  subscription: {
    plan: "free" | "premium" | "pro";
    status: "active" | "canceled" | "expired" | "past_due" | "unpaid";
    billing: "monthly" | "yearly";
    expiresAt: string;
  } | null;
}

interface SubscriptionModalProps {
  user: UserInterface;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedUser: any) => void;
}

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  user,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [activeTab, setActiveTab] = useState<'upgrade' | 'downgrade'>('upgrade');
  const [processing, setProcessing] = useState(false);
  
  // Upgrade form state
  const [upgradeForm, setUpgradeForm] = useState({
    plan: 'pro' as 'pro' | 'premium',
    billing: 'monthly' as 'monthly' | 'yearly',
    duration_days: '',
    reason: '',
    notify_user: true
  });

  // Downgrade form state
  const [downgradeForm, setDowngradeForm] = useState({
    action: 'downgrade_to_free' as 'downgrade_to_free' | 'cancel_immediate' | 'cancel_at_period_end',
    reason: '',
    notify_user: true
  });

  if (!isOpen) return null;

  const handleUpgrade = async () => {
    setProcessing(true);
    
    try {
      const upgradeData: UpgradeUserPlanRequest = {
        user_id: user.id,
        plan: upgradeForm.plan,
        billing: upgradeForm.billing,
        reason: upgradeForm.reason || undefined,
        notify_user: upgradeForm.notify_user
      };

      // Add custom duration if specified
      if (upgradeForm.duration_days && parseInt(upgradeForm.duration_days) > 0) {
        upgradeData.duration_days = parseInt(upgradeForm.duration_days);
      }

      const result = await adminSubscriptionService.upgradeUserPlan(upgradeData);
      
      if (result.success) {
        toast.success('User plan upgraded successfully!');
        onSuccess(result.user);
        onClose();
      } else {
        toast.error(result.error || 'Failed to upgrade user plan');
      }
    } catch (error) {
      console.error('Error upgrading user:', error);
      toast.error('An error occurred while upgrading the user plan');
    } finally {
      setProcessing(false);
    }
  };

  const handleDowngrade = async () => {
    setProcessing(true);
    
    try {
      const downgradeData: DowngradeUserPlanRequest = {
        user_id: user.id,
        action: downgradeForm.action,
        reason: downgradeForm.reason || undefined,
        notify_user: downgradeForm.notify_user
      };

      const result = await adminSubscriptionService.downgradeUserPlan(downgradeData);
      
      if (result.success) {
        toast.success('User subscription updated successfully!');
        onSuccess(result.user);
        onClose();
      } else {
        toast.error(result.error || 'Failed to update user subscription');
      }
    } catch (error) {
      console.error('Error updating user subscription:', error);
      toast.error('An error occurred while updating the user subscription');
    } finally {
      setProcessing(false);
    }
  };

  const getPlanBadge = (plan: string) => {
    switch (plan) {
      case "premium":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-pink-100 text-pink-700">
            <Crown className="h-3 w-3 mr-1" />
            Premium
          </span>
        );
      case "pro":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700">
            <Shield className="h-3 w-3 mr-1" />
            Pro
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            Free
          </span>
        );
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Manage Subscription
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {user.first_name} {user.last_name} - {user.email}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Current Status */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-700">Current Plan</p>
              <div className="mt-1">
                {getPlanBadge(user.subscription?.plan || 'free')}
              </div>
            </div>
            {user.subscription?.expiresAt && (
              <div className="text-right">
                <p className="text-sm font-medium text-gray-700">Expires</p>
                <p className="text-sm text-gray-600 mt-1">
                  {new Date(user.subscription.expiresAt).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('upgrade')}
            className={`flex-1 py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'upgrade'
                ? 'border-orange-500 text-orange-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Upgrade Plan
          </button>
          <button
            onClick={() => setActiveTab('downgrade')}
            className={`flex-1 py-4 px-6 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'downgrade'
                ? 'border-red-500 text-red-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Downgrade / Cancel
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {activeTab === 'upgrade' ? (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Upgrade User Plan</h3>
                
                {/* Plan Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Plan
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        onClick={() => setUpgradeForm({ ...upgradeForm, plan: 'pro' })}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          upgradeForm.plan === 'pro'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Shield className="h-5 w-5 text-orange-600" />
                          <span className="font-medium">Pro Plan</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">$0.99/month or $9.99/year</p>
                      </div>
                      <div
                        onClick={() => setUpgradeForm({ ...upgradeForm, plan: 'premium' })}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          upgradeForm.plan === 'premium'
                            ? 'border-pink-500 bg-pink-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Crown className="h-5 w-5 text-pink-600" />
                          <span className="font-medium">Premium Plan</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">$2.99/month or $29.99/year</p>
                      </div>
                    </div>
                  </div>

                  {/* Billing Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Billing Period
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <div
                        onClick={() => setUpgradeForm({ ...upgradeForm, billing: 'monthly' })}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          upgradeForm.billing === 'monthly'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium">Monthly</span>
                      </div>
                      <div
                        onClick={() => setUpgradeForm({ ...upgradeForm, billing: 'yearly' })}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all ${
                          upgradeForm.billing === 'yearly'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="font-medium">Yearly</span>
                        <span className="text-sm text-green-600 block">Save 17%</span>
                      </div>
                    </div>
                  </div>

                  {/* Custom Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Custom Duration (Optional)
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        min="1"
                        placeholder="Days"
                        value={upgradeForm.duration_days}
                        onChange={(e) => setUpgradeForm({ ...upgradeForm, duration_days: e.target.value })}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                      <span className="text-sm text-gray-600">days</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                      Leave empty for standard billing period
                    </p>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Upgrade
                    </label>
                    <input
                      type="text"
                      placeholder="Admin upgrade for customer support"
                      value={upgradeForm.reason}
                      onChange={(e) => setUpgradeForm({ ...upgradeForm, reason: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    />
                  </div>

                  {/* Notify User */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="notify-upgrade"
                      checked={upgradeForm.notify_user}
                      onChange={(e) => setUpgradeForm({ ...upgradeForm, notify_user: e.target.checked })}
                      className="h-4 w-4 text-orange-600 focus:ring-orange-500 border-gray-300 rounded"
                    />
                    <label htmlFor="notify-upgrade" className="ml-2 block text-sm text-gray-700">
                      Send notification email to user
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpgrade}
                    disabled={processing}
                    className="px-4 py-2 text-sm font-medium text-white bg-orange-600 border border-transparent rounded-md hover:bg-orange-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Upgrade Plan'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">Downgrade / Cancel Subscription</h3>
                
                {/* Action Selection */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Action
                    </label>
                    <div className="space-y-3">
                      <div
                        onClick={() => setDowngradeForm({ ...downgradeForm, action: 'downgrade_to_free' })}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          downgradeForm.action === 'downgrade_to_free'
                            ? 'border-blue-500 bg-blue-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Check className="h-5 w-5 text-blue-600" />
                          <span className="font-medium">Downgrade to Free</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Immediately downgrade to free plan
                        </p>
                      </div>
                      <div
                        onClick={() => setDowngradeForm({ ...downgradeForm, action: 'cancel_immediate' })}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          downgradeForm.action === 'cancel_immediate'
                            ? 'border-red-500 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <AlertCircle className="h-5 w-5 text-red-600" />
                          <span className="font-medium">Cancel Immediately</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Cancel subscription right now
                        </p>
                      </div>
                      <div
                        onClick={() => setDowngradeForm({ ...downgradeForm, action: 'cancel_at_period_end' })}
                        className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                          downgradeForm.action === 'cancel_at_period_end'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-5 w-5 text-orange-600" />
                          <span className="font-medium">Cancel at Period End</span>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          Cancel at the end of current billing period
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Reason */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Change
                    </label>
                    <input
                      type="text"
                      placeholder="Customer request, payment issues, etc."
                      value={downgradeForm.reason}
                      onChange={(e) => setDowngradeForm({ ...downgradeForm, reason: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  </div>

                  {/* Notify User */}
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="notify-downgrade"
                      checked={downgradeForm.notify_user}
                      onChange={(e) => setDowngradeForm({ ...downgradeForm, notify_user: e.target.checked })}
                      className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                    />
                    <label htmlFor="notify-downgrade" className="ml-2 block text-sm text-gray-700">
                      Send notification email to user
                    </label>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDowngrade}
                    disabled={processing}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50"
                  >
                    {processing ? 'Processing...' : 'Update Subscription'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
